import type { LinkProps } from '@tanstack/react-router';
import type {
  GlobalSearchResultsDto,
  GlobalSearchWaitlistHitDto,
} from '@/features/dashboard/globalSearch/types/GlobalSearchResultsDto.ts';

export type GlobalSearchGroupKey = keyof GlobalSearchResultsDto;

export type GlobalSearchListLink = Pick<LinkProps, 'to' | 'search'>;

export interface GlobalSearchGroup {
  key: GlobalSearchGroupKey;
  label: string;
  /** Ekran listy, na który prowadzi „Otwórz listę”. */
  to:
    | '/app/tours'
    | '/app/participants'
    | '/app/clients'
    | '/app/bookings'
    | '/app/waitlist';
  /** Filtr szybki listy, który odsłania anulowane — „Otwórz listę” przy włączonym przełączniku. */
  cancelledQuick?: Record<string, boolean>;
}

/** Jedyne miejsce z etykietami i kolejnością grup — paleta i telefon czytają stąd. */
export const GLOBAL_SEARCH_GROUPS: readonly GlobalSearchGroup[] = [
  { key: 'tours', label: 'Wyjazdy', to: '/app/tours' },
  {
    key: 'participants',
    label: 'Uczestnicy',
    to: '/app/participants',
    cancelledQuick: { hideWithdrawn: false },
  },
  {
    key: 'clients',
    label: 'Zapisujący',
    to: '/app/clients',
    cancelledQuick: { hideCancelled: false },
  },
  {
    key: 'bookings',
    label: 'Rezerwacje',
    to: '/app/bookings',
    cancelledQuick: { hideCancelled: false },
  },
  { key: 'waitlist', label: 'Lista rezerwowa', to: '/app/waitlist' },
];

export const OPEN_LIST_LABEL = 'Otwórz listę';

export const INCLUDE_CANCELLED_LABEL = 'Szukaj też w anulowanych';

export const listLink = (
  group: GlobalSearchGroup,
  term: string,
  includeCancelled: boolean,
): GlobalSearchListLink => ({
  to: group.to,
  search: {
    query: term.trim(),
    ...(includeCancelled && group.cancelledQuick
      ? { quick: group.cancelledQuick }
      : {}),
  },
});

/*
 * Wpis listy rezerwowej nie ma własnego ekranu — otwiera listę zawężoną do tej osoby.
 * Pełne imię i nazwisko wystarczy: kolumna osoby jest przeszukiwalna także jako złożenie
 * imienia z nazwiskiem, a czytelniej wygląda w polu wyszukiwarki niż adres e-mail.
 */
export const waitlistHitLink = (
  hit: GlobalSearchWaitlistHitDto,
): GlobalSearchListLink => ({
  to: '/app/waitlist',
  search: { query: `${hit.client.firstName} ${hit.client.lastName}` },
});

export const hasAnyHit = (results: GlobalSearchResultsDto): boolean =>
  GLOBAL_SEARCH_GROUPS.some((group) => results[group.key].length > 0);
